import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { DetailField, LoadingState, PageHeader, PageLayout, SectionCard } from '../../components/ui';

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'returned', label: 'Returned' },
  { value: 'maintenance', label: 'Maintenance' },
];

const CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'used', label: 'Used' },
  { value: 'damaged', label: 'Damaged' },
];

const titleize = (value) =>
  String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim();

const optionLabel = (option) => {
  if (!option) return '';
  const name = option.name || option.full_name || option.username || '';
  const employeeCode = option.employee_code || option.id || '';
  return name ? `${name} (${employeeCode})` : String(employeeCode);
};

function FieldGroup({ icon, title, description, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        {icon}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function FieldGrid({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 1.2,
      }}
    >
      {children}
    </Box>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center', mt: 0.1 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function EditAssetPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [asset, setAsset] = useState(null);
  const [originalAsset, setOriginalAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const fetchAsset = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get(`/assets/${id}`);
        setAsset(data);
        setOriginalAsset(data);
        if (data.user) setSelectedUser(data.user);
      } catch (err) {
        console.error('Error fetching asset:', err);
        setError('Error fetching asset.');
      } finally {
        setLoading(false);
      }
    };

    fetchAsset();
  }, [id]);

  useEffect(() => {
    const fetchUsers = async () => {
      setUserLoading(true);
      try {
        const res = await api.get('/admin/users');
        const arr = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        setUsers(arr);
      } catch (err) {
        console.error('Error loading employees:', err);
      } finally {
        setUserLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const requiredStatus = useMemo(
    () => [
      { label: 'Asset tag', done: Boolean(asset?.asset_tag) },
      { label: 'Status', done: Boolean(asset?.status) },
      { label: 'Condition', done: Boolean(asset?.condition) },
      { label: 'Assignee', done: asset?.status !== 'assigned' || Boolean(selectedUser?.employee_code) },
    ],
    [asset?.asset_tag, asset?.condition, asset?.status, selectedUser?.employee_code]
  );
  const completionPct = Math.round((requiredStatus.filter((item) => item.done).length / requiredStatus.length) * 100);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
    },
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setAsset((current) => ({ ...current, [name]: value }));
  };

  const handleStatusChange = (event) => {
    const nextStatus = event.target.value;
    setSelectedUser(null);
    setAsset((current) => ({
      ...current,
      assignment_date: '',
      user_employee_code: null,
      status: nextStatus,
    }));
  };

  const handleReset = () => {
    if (!originalAsset) return;
    setError('');
    setAsset(originalAsset);
    setSelectedUser(originalAsset.user || null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!asset) return;

    if (asset.status === 'assigned' && !selectedUser?.employee_code) {
      setError('Please select an employee before assigning this asset.');
      return;
    }

    const payload = {
      ...asset,
      user_employee_code: asset.status === 'assigned' ? selectedUser?.employee_code : null,
      assignment_date:
        asset.status === 'assigned'
          ? asset.assignment_date || new Date().toISOString().split('T')[0]
          : null,
    };

    setSaving(true);
    setError('');
    try {
      await api.put(`/assets/${id}`, payload);
      toast.success('Asset updated successfully.');
      navigate('/assets');
    } catch (err) {
      console.error('Error updating asset:', err);
      setError(err.response?.data?.message || 'Error updating asset.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !asset) {
    return (
      <PageLayout backgroundAccent compact maxWidth="xl">
        <PageHeader
          compact
          variant="dashboard"
          overline="Inventory"
          title="Edit Asset"
          subtitle="Loading asset details..."
        />
        <LoadingState rows={8} />
      </PageLayout>
    );
  }

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        variant="dashboard"
        overline="Inventory"
        title={`Edit Asset ${asset.asset_tag || `#${id}`}`}
        subtitle="Update ownership, condition, and lifecycle status for this asset record."
        actions={[
          <Button
            key="back"
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/assets')}
          >
            Back to Assets
          </Button>,
        ]}
      />

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <SectionCard
          tone="dashboard"
          title="Asset Lifecycle"
          subheader="Keep asset assignment and condition accurate for employee profile and inventory reporting."
          contentSx={{ p: { xs: 1.35, md: 1.7 } }}
        >
          <Stack spacing={1.8}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <FieldGroup
              icon={<Inventory2RoundedIcon color="primary" />}
              title="Identity"
              description="Asset tag remains the primary visible inventory reference."
            >
              <FieldGrid>
                <TextField
                  required
                  label="Asset Tag"
                  name="asset_tag"
                  value={asset.asset_tag || ''}
                  onChange={handleChange}
                  sx={fieldSx}
                />
                <TextField
                  label="Serial Number"
                  name="serial_number"
                  value={asset.serial_number || ''}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </FieldGrid>
            </FieldGroup>

            <Divider />

            <FieldGroup
              icon={<CategoryRoundedIcon color="primary" />}
              title="Status and Condition"
              description="Changing status controls whether assignment fields are required."
            >
              <FieldGrid>
                <TextField
                  select
                  label="Status"
                  name="status"
                  value={asset.status || ''}
                  onChange={handleStatusChange}
                  sx={fieldSx}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Condition"
                  name="condition"
                  value={asset.condition || ''}
                  onChange={handleChange}
                  sx={fieldSx}
                >
                  {CONDITIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="date"
                  label="Return Date"
                  name="return_date"
                  value={asset.return_date || ''}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </FieldGrid>
            </FieldGroup>

            {asset.status === 'assigned' ? (
              <>
                <Divider />
                <FieldGroup
                  icon={<AssignmentIndRoundedIcon color="primary" />}
                  title="Assignment"
                  description="Select the employee who currently owns or uses this asset."
                >
                  <FieldGrid>
                    <Autocomplete
                      options={users}
                      loading={userLoading}
                      value={selectedUser}
                      onChange={(_, value) => setSelectedUser(value)}
                      getOptionLabel={optionLabel}
                      isOptionEqualToValue={(option, value) =>
                        option.employee_code === value.employee_code
                      }
                      filterOptions={(options, { inputValue }) =>
                        options.filter((option) =>
                          optionLabel(option).toLowerCase().includes(inputValue.toLowerCase())
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assign To"
                          required
                          sx={fieldSx}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {userLoading ? <CircularProgress size={18} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      noOptionsText={userLoading ? 'Loading...' : users.length ? 'No match' : 'No employees loaded'}
                    />
                    <TextField
                      required
                      type="date"
                      label="Assignment Date"
                      name="assignment_date"
                      value={asset.assignment_date || ''}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                  </FieldGrid>
                </FieldGroup>
              </>
            ) : null}

            <Divider />

            <FieldGroup
              icon={<NotesRoundedIcon color="primary" />}
              title="Notes"
              description="Capture handover condition, accessory details, or support notes."
            >
              <TextField
                multiline
                minRows={4}
                label="Notes"
                name="notes"
                value={asset.notes || ''}
                onChange={handleChange}
                sx={fieldSx}
              />
            </FieldGroup>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              <Button
                type="button"
                variant="outlined"
                startIcon={<RestartAltRoundedIcon />}
                onClick={handleReset}
                disabled={saving}
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Stack>
        </SectionCard>

        <Stack spacing={1.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <SectionCard
            tone="dashboard"
            title="Asset Summary"
            subheader="Live ownership and status preview."
            contentSx={{ p: 1.6 }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color={completionPct === 100 ? 'success' : 'warning'}
                  label={`${completionPct}% complete`}
                />
                <Chip size="small" variant="outlined" label={titleize(asset.status)} />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={completionPct}
                sx={{ height: 8, borderRadius: 999, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
              />
              <SummaryRow icon={<TagRoundedIcon fontSize="small" />} label="Asset Tag" value={asset.asset_tag} />
              <SummaryRow icon={<Inventory2RoundedIcon fontSize="small" />} label="Serial" value={asset.serial_number} />
              <SummaryRow icon={<AssignmentIndRoundedIcon fontSize="small" />} label="Assigned To" value={selectedUser ? optionLabel(selectedUser) : 'Unassigned'} />
              <SummaryRow icon={<EventAvailableRoundedIcon fontSize="small" />} label="Assignment Date" value={asset.assignment_date} />
              <DetailField
                compact
                label="Condition"
                value={titleize(asset.condition)}
                icon={<VerifiedRoundedIcon fontSize="small" />}
              />
            </Stack>
          </SectionCard>
        </Stack>
      </Box>
    </PageLayout>
  );
}
